/**
 * The landing's cart drawer (handoff: 390px slide-over, quantity steppers, live
 * subtotal, Checkout). It is a view over the SHARED cart store, so a line added
 * here is the same line the shop, cart and checkout pages see; checkout itself
 * is the shared `/checkout` page. No commerce logic lives in this folder.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, X } from 'lucide-react';
import { cartCount, useCartStore } from '../../../storefront/lib/cart-store';
import { peso } from './format';

export function AzCartDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const lines = useCartStore((s) => s.lines);
  const setQty = useCartStore((s) => s.setQty);
  const setCheckedMany = useCartStore((s) => s.setCheckedMany);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes; the page behind stops scrolling while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const list = Object.values(lines);
  const count = cartCount(lines);
  const subtotal = list.reduce((a, l) => a + l.price * l.qty, 0);

  // Checkout charges the CHECKED lines; the drawer shows every line, so tick
  // them all first and the order is exactly what the drawer listed.
  function checkout() {
    setCheckedMany(
      list.map((l) => l.key),
      true,
    );
    onClose();
    navigate('/checkout');
  }

  return (
    <div className="az-drawer-root">
      <button type="button" className="az-drawer-scrim" aria-label="Close cart" onClick={onClose} />
      <div className="az-drawer" role="dialog" aria-modal="true" aria-label="My cart">
        <div className="az-drawer-head">
          <div className="az-drawer-title">
            My Cart <span>({count})</span>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="az-icon-btn"
            aria-label="Close cart"
            onClick={onClose}
          >
            <X size={22} />
          </button>
        </div>

        {list.length === 0 ? (
          <div className="az-drawer-empty">
            <ShoppingCart size={64} strokeWidth={1.6} color="#D8CCBF" aria-hidden />
            <strong>Your cart is empty</strong>
            <button type="button" className="az-browse" onClick={onClose}>
              Browse Products
            </button>
          </div>
        ) : (
          <>
            <div className="az-drawer-lines">
              {list.map((l) => (
                <div key={l.key} className="az-line">
                  {l.image ? (
                    <img className="az-line-thumb" src={l.image} alt="" />
                  ) : (
                    <div className="az-line-thumb" aria-hidden />
                  )}
                  <div className="az-line-main">
                    <div className="az-line-name">{l.name}</div>
                    {l.variation && <div className="az-line-variation">{l.variation}</div>}
                    <div className="az-line-row">
                      <div className="az-line-total">{peso(l.price * l.qty)}</div>
                      <div className="az-stepper">
                        <button
                          type="button"
                          aria-label={`One less ${l.name}`}
                          onClick={() => setQty(l.key, l.qty - 1)}
                        >
                          <Minus size={14} />
                        </button>
                        <span aria-label="Quantity">{l.qty}</span>
                        <button
                          type="button"
                          aria-label={`One more ${l.name}`}
                          disabled={l.isVirtual || (l.stock != null && l.qty >= l.stock)}
                          onClick={() => setQty(l.key, l.qty + 1)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="az-drawer-foot">
              <div className="az-subtotal">
                <span>Subtotal</span>
                <strong>{peso(subtotal)}</strong>
              </div>
              <button type="button" className="az-checkout" onClick={checkout}>
                Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
